import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows } from "./payments.styles";
import NoDataFound from "../../Reports/NoDataFound";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import CircleIcon from "@mui/icons-material/Circle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const RevenueTrend = ({ data = [] }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;
  const [activeIndex, setActiveIndex] = useState(null);

  const chartData = data;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const revenueData = payload.find(p => p.dataKey === "revenue");
    const growthData = payload.find(p => p.dataKey === "growth");

    const formattedRevenue = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(revenueData?.value || 0);

    return (
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          px: 2,
          py: 1.5,
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,.15)",
          border: `1px solid ${colors.borderLight}`,
          minWidth: 140,
        }}
      >
        <Typography fontSize={11} color={colors.textMuted} mb={0.5}>
          {label}, 2023
        </Typography>
        <Typography fontSize={13} fontWeight={600} color={colors.textPrimary} mb={0.5}>
          Daily Revenue
        </Typography>
        <Typography fontSize={18} fontWeight={700} color={colors.textPrimary} mb={1}>
          {formattedRevenue}
        </Typography>
        {growthData && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TrendingUpIcon sx={{ fontSize: 14, color: primaryColor }} />
            <Typography fontSize={12} fontWeight={600} color={primaryColor}>
              +{growthData.value}%
            </Typography>
            <Typography fontSize={11} color={colors.textMuted}>
              vs last week same day
            </Typography>
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
        <Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 0.5,
            }}
          >
            Revenue Trend
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: primaryColor,
            }}
          >
            Daily income performance for current month
          </Typography>
        </Box>

        {/* Legend */}
        <Stack direction="row" spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircleIcon sx={{ fontSize: 8, color: primaryColor }} />
            <Typography fontSize={11} fontWeight={500} color={colors.textSecondary}>
              DAILY REVENUE
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 16,
                height: 2,
                bgcolor: primaryColor,
                borderRadius: 1,
              }}
            />
            <Typography fontSize={11} fontWeight={500} color={colors.textSecondary}>
              GROWTH TREND
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Chart */}
      <Box sx={{ flex: 1, minHeight: 280 }}>
        {chartData.length === 0 ? (
          <NoDataFound />
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
            onMouseMove={(e) => {
              if (e.activeTooltipIndex !== undefined) {
                setActiveIndex(e.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={primaryColor} stopOpacity={0.9} />
                <stop offset="100%" stopColor={primaryColor} stopOpacity={0.6} />
              </linearGradient>
            </defs>

            <CartesianGrid
              vertical={false}
              stroke={colors.borderLight}
              strokeDasharray="4 4"
            />

            <XAxis
              dataKey="date"
              tick={{ fill: colors.textMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              dy={10}
            />

            <YAxis
              yAxisId="left"
              tick={{ fill: colors.textMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => value >= 100000 ? `₹${value / 100000}L` : `₹${value / 1000}k`}
              dx={-10}
              width={50}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: colors.textMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `${value}%`}
              dx={10}
              width={40}
              domain={[0, 20]}
            />

            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />

            <Bar
              yAxisId="left"
              dataKey="revenue"
              fill="url(#barGradient)"
              radius={[4, 4, 0, 0]}
              barSize={10}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="growth"
              stroke={primaryColor}
              strokeWidth={2}
              dot={{
                r: 4,
                fill: "#fff",
                stroke: primaryColor,
                strokeWidth: 2,
              }}
              activeDot={{
                r: 6,
                fill: primaryColor,
                stroke: "#fff",
                strokeWidth: 3,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default RevenueTrend;
